import {
  BulldozerIcon,
  CarProfileIcon,
  Icon,
  MopedFrontIcon,
  MopedIcon,
  MotorcycleIcon,
  TaxiIcon,
  TractorIcon,
} from '@phosphor-icons/react'
import TrailerIcon from '@/src/components/core/icons/TrailerIcon'

export type CategoryColor = 'green' | 'blue' | 'yellow' | 'red'

export type Category = {
  id: string
  code: string
  title: string
  subtitle: string
  description: string
  minAge: string
  requirements: string
  icon: Icon
  color: CategoryColor
}

export const CATEGORY_COLORS: Record<
  CategoryColor,
  { box: string; icon: string }
> = {
  green: {
    box: 'border-green-100 from-green-50 to-green-100',
    icon: 'text-green-800',
  },
  blue: {
    box: 'border-blue-100 from-blue-50 to-blue-100',
    icon: 'text-blue-800',
  },
  yellow: {
    box: 'border-yellow-100 from-yellow-50 to-yellow-100',
    icon: 'text-yellow-800',
  },
  red: {
    box: 'border-red-100 from-red-50 to-red-100',
    icon: 'text-red-800',
  },
}

export const CATEGORY_GROUPS: Category[][] = [
  [
    {
      id: 'kategorie-a',
      code: 'A',
      title: 'Kategorie A',
      subtitle: 'Alle Motorräder',
      description:
        'Mit dem Führerausweis der Kategorie A dürfen alle Motorräder ohne Hubraum- oder Leistungsbeschränkung gelenkt werden. Diese Kategorie gilt als die umfassendste im Bereich der Zweiräder und berechtigt zum Führen von Motorrädern jeder Art, einschliesslich schwerer Maschinen mit hoher Motorleistung.',
      minAge: '18 Jahre',
      requirements:
        'Ein Direkteinstieg in die Kategorie A ist seit 2021 grundsätzlich nicht mehr möglich. Der reguläre Weg führt über die Kategorie A beschränkt mit mindestens 2 Jahren klagloser Fahrpraxis und anschliessender praktischer Prüfung.',
      icon: MotorcycleIcon,
      color: 'green',
    },
    {
      id: 'kategorie-a-beschraenkt',
      code: '-A',
      title: 'Kategorie A beschränkt',
      subtitle: 'Motorräder bis 35 kW',
      description:
        'Die Kategorie A beschränkt erlaubt das Führen von Motorrädern mit einer maximalen Leistung von 35 kW und einem Leistungsgewicht von höchstens 0,2 kW/kg. Sie dient oft als Einstieg in die Kategorie A.',
      minAge: '18 Jahre',
      requirements:
        'Führerausweis Kategorie B empfohlen; Fahrkurs obligatorisch; theoretische und praktische Prüfung.',
      icon: MotorcycleIcon,
      color: 'green',
    },
    {
      id: 'kategorie-a1',
      code: 'A1',
      title: 'Kategorie A1',
      subtitle: 'Leichte Motorräder bis 125 cm³',
      description:
        'Mit der Kategorie A1 dürfen leichte Motorräder mit einem Hubraum von bis zu 125 cm³ und einer maximalen Leistung von 11 kW gelenkt werden. Diese Kategorie richtet sich an Einsteiger im Motorradbereich.',
      minAge:
        '15 Jahre für Kleinmotorräder mit einem Hubraum von höchstens 50 cm³, einer Motorleistung von höchstens 4 kW und maximal 45 km/h; 16 Jahre für die übrigen Motorräder der Unterkategorie A1 (Hubraum bis 125 cm³, Motorleistung bis 11 kW).',
      requirements:
        'Führerausweis Kategorie B empfohlen; obligatorischer Fahrkurs; theoretische Prüfung.',
      icon: MopedIcon,
      color: 'green',
    },
    {
      id: 'kategorie-b',
      code: 'B',
      title: 'Kategorie B',
      subtitle: 'Personenwagen',
      description:
        "Die Kategorie B ist der am weitesten verbreitete Führerausweis in der Schweiz. Sie berechtigt zum Lenken von Personenwagen und leichten Motorfahrzeugen mit einem Gesamtgewicht von bis zu 3'500 kg sowie mit maximal 8 Sitzplätzen (ohne Fahrersitz). Leichte Anhänger bis 750 kg dürfen ohne zusätzliche Kategorie mitgeführt werden.",
      minAge: '18 Jahre (Lernfahrausweis ab 17 Jahren)',
      requirements:
        'Nothelferkurs; Lernfahrausweis; Fahrausbildung; theoretische und praktische Prüfung.',
      icon: CarProfileIcon,
      color: 'blue',
    },
    {
      id: 'kategorie-be',
      code: 'BE',
      title: 'Kategorie BE',
      subtitle: 'Personenwagen mit schwerem Anhänger',
      description:
        "Mit der Kategorie BE dürfen Fahrzeuge der Kategorie B mit einem Anhänger gelenkt werden, dessen Gesamtgewicht 750 kg übersteigt. Sie ist für Fahrzeugkombinationen vorgesehen, die nicht mehr unter die Kategorie B fallen, und wird insbesondere benötigt, wenn die zulässige Gesamtmasse der Fahrzeugkombination mehr als 3'500 kg beträgt.",
      minAge: '18 Jahre',
      requirements:
        'Führerausweis Kategorie B; Lernfahrausweis Kategorie BE; praktische Prüfung.',
      icon: TrailerIcon,
      color: 'blue',
    },
    {
      id: 'kategorie-b1',
      code: 'B1',
      title: 'Kategorie B1',
      subtitle: 'Klein- und dreirädrige Motorfahrzeuge',
      description:
        'Die Kategorie B1 gilt für Klein- und dreirädrige Motorfahrzeuge mit einem Leergewicht von höchstens 670 kg.',
      minAge: '18 Jahre',
      requirements:
        'Nothelferkurs; Lernfahrausweis; Fahrausbildung; theoretische und praktische Prüfung.',
      icon: CarProfileIcon,
      color: 'blue',
    },
  ],
  [
    {
      id: 'kategorie-m',
      code: 'M',
      title: 'Kategorie M',
      subtitle: 'Mofas',
      description:
        'Die Kategorie M gilt für Mofas und leichte Motorfahrräder mit einer bauartbedingten Höchstgeschwindigkeit von bis zu 45 km/h und einem Hubraum von maximal 50 cm³. Sie ist die niedrigste Führerscheinkategorie und ein häufiger erster Schritt für Jugendliche.',
      minAge: '14 Jahre',
      requirements: 'Theoretische Prüfung',
      icon: MopedFrontIcon,
      color: 'yellow',
    },
    {
      id: 'kategorie-f',
      code: 'F',
      title: 'Kategorie F',
      subtitle: 'Motorfahrzeuge bis 45 km/h',
      description:
        'Die Kategorie F berechtigt zum Führen von Motorfahrzeugen mit einer bauartbedingten Höchstgeschwindigkeit von bis zu 45 km/h, wie zum Beispiel Traktoren für nicht-landwirtschaftliche Zwecke, Stapler oder Arbeitsmaschinen.',
      minAge: '18 Jahre (16 Jahre mit Einschränkungen)',
      requirements: 'Theoretische Prüfung; kein Fahrkurs obligatorisch.',
      icon: BulldozerIcon,
      color: 'yellow',
    },
    {
      id: 'kategorie-g',
      code: 'G',
      title: 'Kategorie G',
      subtitle: 'Landwirtschaftliche Fahrzeuge',
      description:
        'Mit der Kategorie G dürfen landwirtschaftliche Motorfahrzeuge wie Traktoren bis zu einer Höchstgeschwindigkeit von 30 km/h gelenkt werden. Diese Kategorie richtet sich an Personen, die in der Land- und Forstwirtschaft tätig sind.',
      minAge: '14 Jahre',
      requirements: 'Theoretische Prüfung; kein Fahrkurs obligatorisch.',
      icon: TractorIcon,
      color: 'yellow',
    },
    {
      id: 'kategorie-bpt',
      code: 'BPT',
      title: 'Kategorie BPT',
      subtitle: 'Berufsmässiger Personentransport',
      description:
        'Die BPT-Bewilligung berechtigt zum berufsmässigen Personentransport mit Fahrzeugen der Kategorien B, B1, C, C1 oder F – zum Beispiel als Taxifahrer, für Schülertransporte oder Ambulanzdienste. Es handelt sich nicht um eine eigenständige Führerausweiskategorie, sondern um eine Zusatzbewilligung. In den Kategorien D und D1 ist sie bereits enthalten. Die Bewilligung wird als Code 121 (Taxi) oder Code 122 (Schüler-, Behinderten-, Arbeitertransporte, Ambulanzen) im Führerausweis eingetragen.',
      minAge: '19 Jahre',
      requirements:
        'Gültiger Führerausweis der jeweiligen Kategorie; mindestens 1 Jahr klaglose Fahrpraxis; vertrauensärztliche Untersuchung; Zusatztheorieprüfung und praktische Prüfung (Code 121). Für Code 122 entfällt die Zusatztheorieprüfung.',
      icon: TaxiIcon,
      color: 'red',
    },
  ],
]

export const CATEGORIES: Category[] = CATEGORY_GROUPS.flat()
